// services/aiStrategyService.js
// ═══════════════════════════════════════════════════════════════════════════════
// AI Strategy Service — thin client that calls backend AI endpoints
//
// CHANGES IN THIS VERSION:
// - Uses REACT_APP_API_BASEURL (matches other services like assetService.js)
// - Forwards marketType, positionMode, and hedge-mode short-side logic to backend
// - Adds Authorization header for consistency with the rest of the app
// - Preserves backwards-compatible constructor signature (ignores apiKey arg)
// ═══════════════════════════════════════════════════════════════════════════════

// ✅ Match the env var used by the rest of the app (assetService, etc.)
const API_BASE_URL = process.env.REACT_APP_API_BASEURL || 'http://localhost:3005';

class AIStrategyService {
  constructor(/* apiKey */) {
    // API key is no longer needed on the frontend — it lives on the backend.
    // Constructor signature preserved for backwards compatibility with
    // existing callers like `new AIStrategyService(apiKey)`.
  }

  /**
   * Build request headers. Includes Authorization if a token is present.
   */
  _buildHeaders() {
    const headers = { 'Content-Type': 'application/json' };
    const token = localStorage.getItem('token');
    if (token) {
      headers['Authorization'] = token;
    }
    return headers;
  }

  /**
   * Generate trading strategy from a natural-language description.
   *
   * @param {string} userPrompt           - Strategy description
   * @param {object} options
   * @param {string} options.interval     - Timeframe (e.g. '1H', 'tick')
   * @param {string} options.symbol       - Trading pair
   * @param {string} options.marketType   - 'spot' | 'futures'
   * @param {string} options.positionMode - 'one_way' | 'hedge'
   * @param {string} options.existingBuyLogic
   * @param {string} options.existingSellLogic
   * @param {string} options.existingShortOpenLogic  - hedge mode only
   * @param {string} options.existingShortCloseLogic - hedge mode only
   * @returns {Promise<object>} Strategy response with buyLogic, sellLogic, etc.
   */
  async generateStrategy(userPrompt, options = {}) {
    const {
      interval = '1H',
      symbol = '',
      marketType = 'spot',
      positionMode = 'one_way',
      existingBuyLogic = '',
      existingSellLogic = '',
      existingShortOpenLogic = '',
      existingShortCloseLogic = '',
      currentRun = null,
      history = [],
    } = options;

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/generate-strategy`, {
        method: 'POST',
        headers: this._buildHeaders(),
        body: JSON.stringify({
          prompt: userPrompt,
          interval,
          symbol,
          marketType,                  // ✅ forwarded to backend
          positionMode,                // ✅ forwarded to backend
          existingBuyLogic,
          existingSellLogic,
          existingShortOpenLogic,      // ✅ hedge-mode context
          existingShortCloseLogic,     // ✅ hedge-mode context
          userId: localStorage.getItem('userId'),
          // A backtest that just finished but hasn't been saved yet. Saved runs
          // are looked up server-side; this covers the tweak → run → tweak loop.
          currentRun,
          // Prior turns of this conversation, EXCLUDING the current prompt —
          // the backend appends that itself. Without this the endpoint is
          // one-shot and follow-ups have nothing to refer back to.
          history,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const result = await response.json();

      if (!result || typeof result !== 'object') {
        throw new Error('Invalid response from server');
      }

      // Return in a stable format the UI can rely on.
      return {
        success: result.success ?? false,
        buyLogic: result.buyLogic || '',
        sellLogic: result.sellLogic || '',
        shortOpenLogic: result.shortOpenLogic || '',      // ✅ hedge mode
        shortCloseLogic: result.shortCloseLogic || '',    // ✅ hedge mode
        explanation: result.explanation || '',
        warnings: result.warnings || [],
        suggestions: result.suggestions || [],
        validation: result.validation || { isValid: true, errors: [], warnings: [] },
        indicatorsUsed: result.indicatorsUsed || [],
        tokensUsed: result.tokensUsed,
        model: result.model,
        context: result.context,
        // The advisor can answer conversationally instead of emitting a
        // strategy; without these the reply was silently dropped and the caller
        // just saw empty logic.
        conversational: !!result.conversational,
        reply: result.reply || '',
      };
    } catch (error) {
      console.error('AI Strategy generation failed:', error);

      return {
        success: false,
        buyLogic: '',
        sellLogic: '',
        shortOpenLogic: '',
        shortCloseLogic: '',
        explanation: `Failed to generate strategy: ${error.message}`,
        warnings: [],
        suggestions: [],
        validation: { isValid: false, errors: [error.message], warnings: [] },
      };
    }
  }

  /**
   * Refine / modify an existing strategy.
   *
   * Two call signatures are supported for backwards compatibility:
   *
   *   (A) Legacy positional:
   *       refineStrategy(currentBuy, currentSell, refinementRequest, interval)
   *
   *   (B) Modern named-options:
   *       refineStrategy({
   *         refinement,
   *         currentBuyLogic, currentSellLogic,
   *         currentShortOpenLogic, currentShortCloseLogic,
   *         interval, symbol, marketType, positionMode,
   *       })
   */
  async refineStrategy(...args) {
    // Detect call style
    let payload;
    if (args.length === 1 && typeof args[0] === 'object' && args[0] !== null) {
      // Modern named-options call
      const {
        refinement = '',
        currentBuyLogic = '',
        currentSellLogic = '',
        currentShortOpenLogic = '',
        currentShortCloseLogic = '',
        interval = '1H',
        symbol = '',
        marketType = 'spot',
        positionMode = 'one_way',
        currentRun = null,
        history = [],
      } = args[0];
      payload = {
        currentBuyLogic,
        currentSellLogic,
        currentShortOpenLogic,
        currentShortCloseLogic,
        refinement,
        interval,
        symbol,
        marketType,
        positionMode,
        userId: localStorage.getItem('userId'),
        // Unsaved backtest result to reason about (see generateStrategy).
        currentRun,
        history,
      };
    } else {
      // Legacy positional call
      const [currentBuy = '', currentSell = '', refinementRequest = '', interval = '1H'] = args;
      payload = {
        currentBuyLogic: currentBuy,
        currentSellLogic: currentSell,
        currentShortOpenLogic: '',
        currentShortCloseLogic: '',
        refinement: refinementRequest,
        interval,
        symbol: '',
        marketType: 'spot',
        positionMode: 'one_way',
        userId: localStorage.getItem('userId'),
      };
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/ai/refine-strategy`, {
        method: 'POST',
        headers: this._buildHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }

      const result = await response.json();

      return {
        success: result.success ?? false,
        buyLogic: result.buyLogic || '',
        sellLogic: result.sellLogic || '',
        shortOpenLogic: result.shortOpenLogic || '',
        shortCloseLogic: result.shortCloseLogic || '',
        explanation: result.explanation || '',
        warnings: result.warnings || [],
        suggestions: result.suggestions || [],
        validation: result.validation || { isValid: true, errors: [], warnings: [] },
        indicatorsUsed: result.indicatorsUsed || [],
        tokensUsed: result.tokensUsed,
        model: result.model,
        context: result.context,
        // The advisor can answer conversationally instead of emitting a
        // strategy; without these the reply was silently dropped and the caller
        // just saw empty logic.
        conversational: !!result.conversational,
        reply: result.reply || '',
      };
    } catch (error) {
      console.error('AI Strategy refinement failed:', error);

      return {
        success: false,
        // Preserve original logic on failure so the user doesn't lose work.
        buyLogic: payload.currentBuyLogic,
        sellLogic: payload.currentSellLogic,
        shortOpenLogic: payload.currentShortOpenLogic,
        shortCloseLogic: payload.currentShortCloseLogic,
        explanation: `Failed to refine strategy: ${error.message}`,
        warnings: [],
        suggestions: [],
      };
    }
  }
}

export default AIStrategyService;